/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: RingGroupMembersSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useEffect, useRef, useState } from 'react';
import { GripVertical, Plus, Users } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import { FormBox } from '../../components/ui/FormBox';
import { Button } from '../../components/ui/button';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { MemberRow } from './types/ringGroups.types';
import { MemberRowItem } from './components/MemberRowItem';
import { ExtensionSearchInput, type ExtensionOption } from './components/ExtensionSearchInput';

export type { MemberRow, ExtensionOption };

interface RingGroupMembersSectionProps {
  members: MemberRow[];
  ringTimeout: string;
  onChange: (members: MemberRow[]) => void;
}

const resequenceMemberRows = (rows: MemberRow[]): MemberRow[] =>
  rows.map((row, index) => {
    const newOrderIndex = String(index + 1);
    // Only create new object if orderIndex actually changed
    if (row.orderIndex === newOrderIndex) {
      return row;
    }
    return { ...row, orderIndex: newOrderIndex };
  });

// Helper function to compare if two member arrays are effectively the same
const areMembersEqual = (a: MemberRow[], b: MemberRow[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const memberA = a[i];
    const memberB = b[i];
    if (
      memberA.id !== memberB.id ||
      memberA.extension !== memberB.extension ||
      memberA.orderIndex !== memberB.orderIndex ||
      memberA.delay !== memberB.delay ||
      memberA.timeout !== memberB.timeout ||
      memberA.prompt !== memberB.prompt ||
      memberA.description !== memberB.description ||
      memberA.enabled !== memberB.enabled ||
      memberA.__tempId !== memberB.__tempId
    ) {
      return false;
    }
  }
  return true;
};

const newMember = (priority: number, ringTimeout: string): MemberRow => ({
  id: undefined,
  extension: '',
  orderIndex: String(priority),
  delay: '0',
  timeout: ringTimeout,
  prompt: '',
  description: '',
  enabled: true
});

export function RingGroupMembersSection({
  members,
  ringTimeout,
  onChange
}: RingGroupMembersSectionProps) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const [memberRows, setMemberRows] = useState<MemberRow[]>(members);
  const [availableExtensions, setAvailableExtensions] = useState<ExtensionOption[]>([]);
  const nextMemberIdRef = useRef(-1);
  const extensionsLoadedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const isInternalUpdateRef = useRef(false);
  const isExternalSyncRef = useRef(false); // Track if we're syncing from props
  const previousMembersRef = useRef(members);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // Keep onChangeRef in sync
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync local state with props only when members prop changes externally
  useEffect(() => {
    // Sync useEffect triggered

    // Skip sync if this is an internal update (to avoid loops)
    if (isInternalUpdateRef.current) {
      // Skipping sync - internal update
      // Still update the ref to track the current value
      previousMembersRef.current = members;
      return;
    }

    // Check if members prop actually changed from outside (compare content, not just reference)
    const membersChanged =
      members !== previousMembersRef.current &&
      !areMembersEqual(members, previousMembersRef.current);

    // Members changed check

    // Always update the ref to track current value (prevents infinite loops)
    previousMembersRef.current = members;

    // Only sync state if content actually changed
    if (membersChanged) {
      // Syncing from props - content changed
      // Mark that we're doing an external sync to prevent notification loop
      isExternalSyncRef.current = true;

      // When syncing, preserve __tempId from local state for members that match
      setMemberRows((prevLocal) => {
        // setMemberRows callback - syncing from props

        // First check if local state is already in sync with prop (quick check)
        if (areMembersEqual(prevLocal, members)) {
          // Local state already in sync, returning same reference
          isExternalSyncRef.current = false; // Reset flag before returning
          return prevLocal; // No need to update - return same reference
        }

        // Also check if prevLocal is already equal to what we would create
        // This prevents unnecessary state updates when the merge would result in the same content

        // Create maps for different matching strategies
        const byIdMap = new Map<number, MemberRow>();
        const byExtensionMap = new Map<string, MemberRow>();
        const newMembersWithoutId: MemberRow[] = [];

        prevLocal.forEach((m) => {
          if (m.id !== undefined && m.id !== null) {
            byIdMap.set(m.id, m);
          }
          if (m.extension) {
            byExtensionMap.set(m.extension, m);
          }
          // Track new members (no id, no extension yet)
          if (!m.id && !m.extension && m.__tempId) {
            newMembersWithoutId.push(m);
          }
        });

        // Merge new members with existing __tempId when possible
        const merged: MemberRow[] = [];
        const usedTempIds = new Set<number>();

        members.forEach((newMember) => {
          let matched: MemberRow | null = null;

          // First, try to match by ID (existing members)
          if (newMember.id !== undefined && newMember.id !== null) {
            matched = byIdMap.get(newMember.id) || null;
          }

          // If no match by ID, try by extension (for new members that got saved)
          if (!matched && newMember.extension) {
            matched = byExtensionMap.get(newMember.extension) || null;
          }

          // If we found a match with __tempId, preserve it
          if (matched && matched.__tempId && !newMember.id) {
            const tempId = matched.__tempId!;
            usedTempIds.add(tempId);
            merged.push({ ...newMember, __tempId: tempId });
          } else {
            // No match found, use the new member as-is
            merged.push(newMember);
          }
        });

        // Add any new members from local state that weren't in the prop
        // This handles the case where a new member was just added (no extension yet)
        // and the parent hasn't updated the prop yet, or the member was filtered out
        newMembersWithoutId.forEach((localNewMember) => {
          const tempId = localNewMember.__tempId!;
          if (!usedTempIds.has(tempId)) {
            // Check if this member is already in merged (by tempId)
            const exists = merged.some((m) => m.__tempId === tempId);
            if (!exists) {
              merged.push(localNewMember);
            }
          }
        });

        // Also check if we have more members in local state than in prop
        // This can happen when a new member is added but prop hasn't updated yet
        if (prevLocal.length > merged.length) {
          // Find members in local state that aren't in merged
          prevLocal.forEach((localMember) => {
            const tempId = localMember.__tempId;
            // If it's a new member (no id, no extension) and not in merged, add it
            if (!localMember.id && !localMember.extension && tempId) {
              const exists = merged.some((m) => m.__tempId === tempId);
              if (!exists) {
                merged.push(localMember);
              }
            }
          });
        }

        // Only update if the merged result is actually different from prevLocal
        const mergedEqual = areMembersEqual(prevLocal, merged);
        // Merge result

        if (mergedEqual) {
          // Merged result equals prevLocal, returning same reference
          isExternalSyncRef.current = false; // Reset flag before returning
          return prevLocal; // Return same reference to avoid unnecessary re-render
        }

        // Returning merged result (different from prevLocal)
        // Keep flag true - it will be reset in the memberRows useEffect
        // This ensures the notification useEffect knows this is an external sync
        return merged;
      });
    } else {
      // Members didn't change, skipping sync
      // Members didn't change, ensure flag is reset
      isExternalSyncRef.current = false;
    }
  }, [members]);

  // Notify parent when memberRows change due to internal updates (user actions)
  const prevMemberRowsRef = useRef(memberRows);
  useEffect(() => {
    // Notify useEffect triggered

    // Skip notification if this is an external sync (from props)
    if (isExternalSyncRef.current) {
      // Skipping notification - external sync
      // Just update the ref to track current value, but don't notify parent
      prevMemberRowsRef.current = memberRows;
      // Reset the flag after handling the external sync
      isExternalSyncRef.current = false;
      return;
    }

    // Only notify if this was an internal update and memberRows actually changed
    if (isInternalUpdateRef.current) {
      const contentChanged = !areMembersEqual(prevMemberRowsRef.current, memberRows);
      // Internal update detected

      // Check if content actually changed, not just reference
      if (contentChanged) {
        // Notifying parent - content changed
        prevMemberRowsRef.current = memberRows;
        // Reset flag and notify parent
        isInternalUpdateRef.current = false;
        onChangeRef.current(memberRows);
      } else {
        // Content same, not notifying parent
        // Content is the same, just reset the flag without notifying
        isInternalUpdateRef.current = false;
        prevMemberRowsRef.current = memberRows;
      }
    } else {
      // External change, updating ref only
      // Update ref for external changes to keep it in sync
      prevMemberRowsRef.current = memberRows;
    }
  }, [memberRows]);

  // Load available extensions
  useEffect(() => {
    if (extensionsLoadedRef.current) {
      return;
    }

    extensionsLoadedRef.current = true;

    const limit = 100;
    const maxPages = 20;
    const collected: ExtensionOption[] = [];

    const loadExtensions = async () => {
      try {
        for (let page = 1; page <= maxPages; page += 1) {
          const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sort: 'extension',
            order: 'asc'
          });

          const response = await fetch(buildApiUrl(`/api/telephony/extensions?${params.toString()}`), {
            headers: getApiHeaders()
          });

          if (!response.ok) {
            throw new Error('HTTP_ERROR');
          }

          const result = await response.json();
          const items = Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result?.data?.items)
              ? result.data.items
              : [];

          const mapped = items
            .map((item: { extension?: unknown; name?: unknown }) => {
              const extension = item?.extension?.toString();
              if (!extension) {
                return null;
              }

              const name = item?.name?.toString() ?? '';
              const label = name ? `${extension} � ${name}` : extension;
              const searchText = `${extension} ${name}`.trim().toLowerCase();

              return {
                value: extension,
                label,
                description: name || undefined,
                searchText
              } satisfies ExtensionOption;
            })
            .filter((option: ExtensionOption | null): option is ExtensionOption => option !== null);

          collected.push(...mapped);

          const totalPages = result?.pagination?.totalPages;

          if (items.length < limit) {
            break;
          }

          if (typeof totalPages === 'number' && page >= totalPages) {
            break;
          }
        }

        setAvailableExtensions(collected);
      } catch (error) {
        extensionsLoadedRef.current = false;
        showError(t('ringGroups.messages.extensionsLoadError'));
      }
    };

    void loadExtensions();
  }, [showError, t]);

  const handleMemberDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setMemberRows((items) => {
        // Find indices by matching IDs (id, __tempId, or index)
        // Use nullish coalescing (??) to properly handle 0, false, or empty string values
        const oldIndex = items.findIndex((m, i) => {
          const id = m.id ?? m.__tempId ?? i;
          return id === active.id || id === Number(active.id);
        });
        const newIndex = items.findIndex((m, i) => {
          const id = m.id ?? m.__tempId ?? i;
          return id === over.id || id === Number(over.id);
        });

        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }

        const updated = resequenceMemberRows(arrayMove(items, oldIndex, newIndex));
        // Mark as internal update - useEffect will handle notification
        isInternalUpdateRef.current = true;
        return updated;
      });
    }
  };

  const addMemberRow = () => {
    const timeout = ringTimeout || '30';
    setMemberRows((prev) => {
      const newId = nextMemberIdRef.current--; // Decrement for unique negative IDs
      const newOrderIndex = String(prev.length + 1);
      const newMem: MemberRow = {
        id: undefined, // Explicit: no id for new members
        extension: '',
        orderIndex: newOrderIndex,
        delay: '0',
        timeout: timeout,
        prompt: '',
        description: '',
        enabled: true
      };
      // Always assign a temporary stable ID for React keys and DnD
      (newMem as MemberRow).__tempId = newId;
      // Don't resequence existing members, just append the new one
      // This prevents unnecessary re-renders of existing members
      const updated = [...prev, newMem];
      // Mark as internal update - useEffect will handle notification
      isInternalUpdateRef.current = true;
      return updated;
    });
  };

  const removeMemberRow = (index: number) => {
    setMemberRows((prev) => {
      const updated = resequenceMemberRows(prev.filter((_, i) => i !== index));
      // Mark as internal update - useEffect will handle notification
      isInternalUpdateRef.current = true;
      return updated;
    });
  };

  const updateMemberRow = (index: number, field: keyof MemberRow, value: MemberRow[keyof MemberRow]) => {
    setMemberRows((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      const member = prev[index];
      // Only update if value actually changed
      if (member[field as keyof MemberRow] === value) {
        return prev;
      }
      // Create new array with updated member, preserving __tempId
      const updated = prev.map((m, i) => {
        if (i === index) {
          const newMember = { ...m, [field]: value };
          // Preserve __tempId if it exists
          if ((m as MemberRow).__tempId) {
            (newMember as MemberRow).__tempId = m.__tempId;
          }
          return newMember;
        }
        return m;
      });
      // Mark as internal update - useEffect will handle notification
      isInternalUpdateRef.current = true;
      return updated;
    });
  };

  return (
    <FormBox title={t('ringGroups.form.sections.members')} icon={<Users className="h-5 w-5" />}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('ringGroups.form.members.description')}
        </p>

        <div className="space-y-2">
          {memberRows.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-muted-foreground">
              {t('ringGroups.form.members.empty')}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleMemberDragEnd}
            >
              <SortableContext
                items={memberRows.map((m, i) => m.id ?? m.__tempId ?? i)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <span className="col-span-3 flex items-center gap-2">
                      <GripVertical className="h-3 w-3 opacity-0" />
                      {t('ringGroups.form.members.fields.extension.label')}
                    </span>
                    <span className="col-span-2">
                      {t('ringGroups.form.members.fields.delay.label')}
                    </span>
                    <span className="col-span-2">
                      {t('ringGroups.form.members.fields.timeout.label')}
                    </span>
                    <span className="col-span-1">
                      {t('ringGroups.form.members.fields.confirmPrompt.label')}
                    </span>
                    <span className="col-span-1">
                      {t('ringGroups.form.members.fields.enabled.label')}
                    </span>
                    <span className="col-span-2">
                      {t('ringGroups.form.members.fields.description.label')}
                    </span>
                    <span className="col-span-1 text-right">
                      {t('common.actions')}
                    </span>
                  </div>
                  {memberRows.map((member, index) => {
                    // Use stable ID for key: member.id if exists, otherwise __tempId, fallback to index
                    // Use nullish coalescing (??) to properly handle 0, false, or empty string values
                    const key = member.id ?? member.__tempId ?? `temp-${index}`;
                    return (
                      <MemberRowItem
                        key={key}
                        index={index}
                        member={member}
                        onUpdate={updateMemberRow}
                        onRemove={removeMemberRow}
                        availableExtensions={availableExtensions}
                        t={t}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={addMemberRow}>
            <Plus className="h-4 w-4" />
            <span>{t('ringGroups.form.members.actions.add')}</span>
          </Button>
        </div>
      </div>
    </FormBox>
  );
}

